import { useLocation } from "wouter";
import { 
  Menu, Home, Gift, Settings, Store, Info, Mail, LogOut, 
  ChevronRight, X, Shield, Users, HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import UserAvatar from "./UserAvatar";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { useQuery } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { RoleType } from "@shared/permissions";
const eatzyLogo = "/eatzy-disc-1024.png";

interface NavItem {
  label: string;
  icon: typeof Home;
  path: string;
  description?: string;
}

const mainNavItems: NavItem[] = [
  { label: "Friends", icon: Users, path: "/friends", description: "Activity & social feed" },
  { label: "Rewards", icon: Gift, path: "/rewards", description: "Kudoz points & redemptions" },
  { label: "Gift Certificates", icon: Gift, path: "/gift-certificates", description: "View & purchase" },
  { label: "Settings", icon: Settings, path: "/profile/settings/preferences", description: "Notifications & preferences" },
];

const secondaryNavItems: NavItem[] = [
  { label: "Add Your Restaurant", icon: Store, path: "/owner", description: "Get listed on Eatzy" },
  { label: "FAQ", icon: HelpCircle, path: "/faq" },
  { label: "About", icon: Info, path: "/about" },
  { label: "Contact Us", icon: Mail, path: "/contact" },
];

interface NavigationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function NavigationDrawer({ open, onOpenChange }: NavigationDrawerProps) {
  const [location, setLocation] = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const profileContext = useUserProfileOptional();
  const profile = profileContext?.profile;

  const { data: roleData } = useQuery<{ role: string }>({
    queryKey: ['/api/user/role'],
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = roleData?.role === 'admin' || roleData?.role === 'super_admin';
  const { hasRole, isPortalUser } = usePermissions();

  const navigateTo = (path: string) => {
    setLocation(path);
    onOpenChange(false);
  };

  const handleLogout = () => {
    onOpenChange(false);
    logout();
    setLocation('/');
  };

  const isActive = (path: string) => {
    if (path === "/") return location === "/";
    return location.startsWith(path);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] p-0 flex flex-col">
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
          <SheetDescription>Main navigation for Eatzy Kudoz</SheetDescription>
        </SheetHeader>

        <div className="p-5 pb-4 border-b border-border">
          <div className="flex items-center gap-3 mb-4">
            <img src={eatzyLogo} alt="Eatzy" className="w-10 h-10" />
            <div>
              <h2 className="font-bold text-lg leading-none">Eatzy</h2>
              <p className="text-xs text-muted-foreground">Kudoz</p>
            </div>
          </div>

          {profile && (
            <button
              onClick={() => navigateTo("/profile")}
              className="flex items-center gap-3 w-full rounded-lg p-2 -mx-2 hover-elevate active-elevate-2 transition-colors"
            >
              <UserAvatar
                src={profile.profilePicture}
                name={profile.displayName || profile.name || "User"}
                size="md"
              />
              <div className="flex-1 text-left min-w-0">
                <p className="font-medium text-sm truncate">
                  {profile.displayName || profile.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {profile.email}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          <div className="px-3 py-1">
            {mainNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigateTo(item.path)}
                className={`flex items-center gap-3 w-full rounded-lg px-3 py-3 text-left transition-colors hover-elevate active-elevate-2 ${
                  isActive(item.path) 
                    ? "bg-primary/10 text-primary" 
                    : ""
                }`}
                data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className={`w-5 h-5 shrink-0 ${
                  isActive(item.path) ? "text-primary" : "text-muted-foreground"
                }`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.description && (
                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          <Separator className="my-2" />

          <div className="px-3 py-1">
            {secondaryNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigateTo(item.path)}
                className={`flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left transition-colors hover-elevate active-elevate-2 ${
                  isActive(item.path) 
                    ? "bg-primary/10 text-primary" 
                    : ""
                }`}
                data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className={`w-5 h-5 shrink-0 ${
                  isActive(item.path) ? "text-primary" : "text-muted-foreground"
                }`} />
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>

          {(isPortalUser() || isAdmin) && (
            <>
              <Separator className="my-2" />
              <div className="px-3 py-1">
                <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Portals
                </p>
                {hasRole(RoleType.RestaurantManager) && (
                  <button
                    onClick={() => navigateTo("/partner")}
                    className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left transition-colors hover-elevate active-elevate-2"
                    data-testid="nav-item-partner-portal"
                  >
                    <Store className="w-5 h-5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="text-sm">Partner Portal</span>
                      <span className="text-xs text-muted-foreground">Manage your restaurant</span>
                    </div>
                    <ChevronRight className="w-4 h-4 ml-auto shrink-0 text-muted-foreground" />
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => navigateTo("/admin")}
                    className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left transition-colors hover-elevate active-elevate-2"
                    data-testid="nav-item-admin-portal"
                  >
                    <Shield className="w-5 h-5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="text-sm">Admin Portal</span>
                      <span className="text-xs text-muted-foreground">Platform administration</span>
                    </div>
                    <ChevronRight className="w-4 h-4 ml-auto shrink-0 text-muted-foreground" />
                  </button>
                )}
              </div>
            </>
          )}
        </nav>

        <div className="border-t border-border p-3">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left text-destructive hover-elevate active-elevate-2 transition-colors"
            data-testid="nav-item-logout"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">Log Out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
